/**
 * Единый сервис расчёта расхода топлива.
 * 
 * ВСЕ расчёты топлива в приложении ДОЛЖНЫ использовать эти функции:
 * - WaybillDetail.tsx
 * - WaybillCheckModal.tsx
 * - batchWaybillService.ts
 * - waybillCalculations.ts
 * 
 * Алгоритмы:
 * - BOILER: Сумма отрезков → округление до целого → расчёт расхода → округление до сотых
 * - SEGMENTS: По каждому отрезку с модификаторами (город/прогрев/горы)
 * - MIXED: Усреднённая норма из отрезков, применённая к общему пробегу
 */

import { Route, SeasonSettings } from '../types';

// ============================================================================
// ТИПЫ
// ============================================================================

export type FuelCalculationMethod = 'BOILER' | 'SEGMENTS' | 'MIXED';

export interface FuelRates {
    summerRate: number;
    winterRate: number;
    cityIncreasePercent?: number;
    warmingIncreasePercent?: number;
    mountainIncreasePercent?: number;  // COEF-MOUNTAIN-001
}

export interface FuelCalculationInput {
    routes: Route[];
    rates: FuelRates;
    baseDate: string;
    seasonSettings: SeasonSettings | null;
    dayMode?: 'single' | 'multi';
    odometerDistance?: number;  // Для MIXED: пробег по одометру
}

export interface FuelCalculationResult {
    /** Суммарный пробег (округлённый до целого) */
    distance: number;
    /** Расход топлива (округлённый до сотых) */
    consumption: number;
    /** Расчётный одометр на конец */
    odometerEnd?: number;
    /** Детализация модификаторов (для отображения в UI) */
    modifiers?: ModifierBreakdown;
}

/** Детализация расхода по модификаторам */
export interface ModifierBreakdown {
    /** Базовый расход без модификаторов */
    baseConsumption: number;
    /** Надбавка за город (литры) */
    cityLiters: number;
    /** Процент за город */
    cityPercent: number;
    /** Надбавка за прогрев (литры) */
    warmingLiters: number;
    /** Процент за прогрев */
    warmingPercent: number;
    /** Надбавка за горы (литры) */
    mountainLiters: number;
    /** Процент за горы */
    mountainPercent: number;
}

// ============================================================================
// ОПРЕДЕЛЕНИЕ СЕЗОНА (ЕДИНСТВЕННАЯ РЕАЛИЗАЦИЯ)
// ============================================================================

/**
 * Определяет, является ли дата зимним периодом.
 * ЕДИНСТВЕННАЯ функция определения сезона во всём приложении.
 */
export const isWinterDate = (dateStr: string, settings: SeasonSettings | null): boolean => {
    if (!settings) return false;
    const date = new Date(dateStr);
    if (settings.type === 'manual') {
        if (!settings.winterStartDate || !settings.winterEndDate) return false;
        const start = new Date(settings.winterStartDate);
        const end = new Date(settings.winterEndDate);
        return date >= start && date <= end;
    } else {
        const m = date.getMonth() + 1;
        const d = date.getDate();
        const winterMonth = settings.winterMonth || 11;
        const winterDay = settings.winterDay || 1;
        const summerMonth = settings.summerMonth || 4;
        const summerDay = settings.summerDay || 1;

        // Compare (month, day) tuples for precise season boundary
        const isAfterWinterStart = m > winterMonth || (m === winterMonth && d >= winterDay);
        const isBeforeSummerStart = m < summerMonth || (m === summerMonth && d < summerDay);

        return isAfterWinterStart || isBeforeSummerStart;
    }
};

// ============================================================================
// ВСПОМОГАТЕЛЬНЫЕ ФУНКЦИИ
// ============================================================================

/**
 * Определяет базовую норму расхода (зима/лето)
 * Экспортируется для использования в UI компонентах
 */
export const getBaseRateForDate = (date: string, rates: FuelRates, seasonSettings: SeasonSettings | null): number => {
    // [CRITICAL FIX] Handle case where rates might be undefined/null due to bad data import
    if (!rates) {
        console.warn('[FuelService] getBaseRateForDate called with no rates!');
        return 0;
    }

    const isWinter = isWinterDate(date, seasonSettings);
    return isWinter
        ? (rates.winterRate || rates.summerRate || 0)
        : (rates.summerRate || rates.winterRate || 0);
};

/**
 * Суммирует расстояние всех отрезков (без округления)
 */
const sumRouteDistances = (routes: Route[]): number => {
    return routes.reduce((sum, r) => sum + (Number(r.distanceKm) || 0), 0);
};

// ============================================================================
// АЛГОРИТМ 1: BOILER (По котлу)
// ============================================================================
/**
 * Расчёт по котлу — только база, без модификаторов.
 * 
 * Алгоритм:
 * 1. Суммировать все отрезки пути
 * 2. Округлить до целого
 * 3. Произвести расчёт расхода (км / 100 * норма)
 * 4. Округлить расход до сотых
 * 
 * @param input Входные данные расчёта
 * @returns Результат с distance и consumption
 */
export const calculateBoiler = (input: FuelCalculationInput): FuelCalculationResult => {
    const { routes, rates, baseDate, seasonSettings } = input;

    // 1. Суммировать все отрезки
    const rawDistance = sumRouteDistances(routes);

    // 2. Округлить до целого
    const distance = Math.round(rawDistance);

    // 3. Получить базовую норму (зима/лето)
    const baseRate = getBaseRateForDate(baseDate, rates, seasonSettings);

    // 4. Расчёт: (км / 100) * норма
    const rawConsumption = (distance / 100) * baseRate;

    // 5. Округлить до сотых
    const consumption = Math.round(rawConsumption * 100) / 100;

    return { distance, consumption };
};

// ============================================================================
// АЛГОРИТМ 2: SEGMENTS (По отрезкам)
// ============================================================================
/**
 * Расчёт по отрезкам — с учётом модификаторов (город/прогрев/горы) на каждом отрезке.
 * 
 * Алгоритм:
 * 1. Для каждого отрезка:
 *    - Определить базовую норму (зима/лето по дате отрезка)
 *    - Применить модификаторы (город, прогрев, горы)
 *    - Рассчитать расход отрезка
 * 2. Просуммировать расходы всех отрезков
 * 3. Округлить итоговый расход до сотых
 * 
 * @param input Входные данные расчёта
 * @returns Результат с distance и consumption
 */
export const calculateSegments = (input: FuelCalculationInput): FuelCalculationResult => {
    const { routes, rates, baseDate, seasonSettings, dayMode = 'multi' } = input;

    let totalConsumption = 0;
    let totalBaseConsumption = 0;
    let totalCityLiters = 0;
    let totalWarmingLiters = 0;
    let totalMountainLiters = 0;

    const cityPercent = rates.cityIncreasePercent || 0;
    const warmingPercent = rates.warmingIncreasePercent || 0;
    const mountainPercent = rates.mountainIncreasePercent || 0;

    for (const route of routes) {
        const distanceKm = Number(route.distanceKm) || 0;
        if (distanceKm === 0) continue;

        // Дата отрезка (для определения сезона)
        const routeDate = (dayMode === 'multi' && route.date) ? route.date : baseDate;

        // Базовая норма для этого отрезка
        const baseRate = getBaseRateForDate(routeDate, rates, seasonSettings);

        // Базовый расход отрезка (без модификаторов)
        const segmentBaseConsumption = (distanceKm / 100) * baseRate;
        totalBaseConsumption += segmentBaseConsumption;

        // Расчёт надбавок по каждому модификатору
        let segmentModifierConsumption = 0;

        if (route.isCityDriving && cityPercent > 0) {
            const cityAdd = segmentBaseConsumption * (cityPercent / 100);
            totalCityLiters += cityAdd;
            segmentModifierConsumption += cityAdd;
        }

        if (route.isWarming && warmingPercent > 0) {
            const warmingAdd = segmentBaseConsumption * (warmingPercent / 100);
            totalWarmingLiters += warmingAdd;
            segmentModifierConsumption += warmingAdd;
        }

        // COEF-MOUNTAIN-001: Горная местность
        if (route.isMountainDriving && mountainPercent > 0) {
            const mountainAdd = segmentBaseConsumption * (mountainPercent / 100);
            totalMountainLiters += mountainAdd;
            segmentModifierConsumption += mountainAdd;
        }

        totalConsumption += segmentBaseConsumption + segmentModifierConsumption;
    }

    // Общий пробег (округлённый до целого)
    const distance = Math.round(sumRouteDistances(routes));

    // Округлить расход до сотых
    const consumption = Math.round(totalConsumption * 100) / 100;

    // Детализация модификаторов
    const modifiers: ModifierBreakdown = {
        baseConsumption: Math.round(totalBaseConsumption * 100) / 100,
        cityLiters: Math.round(totalCityLiters * 100) / 100,
        cityPercent,
        warmingLiters: Math.round(totalWarmingLiters * 100) / 100,
        warmingPercent,
        mountainLiters: Math.round(totalMountainLiters * 100) / 100,
        mountainPercent
    };

    return { distance, consumption, modifiers };
};

// ============================================================================
// АЛГОРИТМ 3: MIXED (Смешанный)
// ============================================================================
/**
 * Смешанный расчёт — усреднённая норма из отрезков, применённая к общему пробегу.
 * 
 * Алгоритм:
 * 1. Рассчитать суммарный расход по отрезкам (как SEGMENTS)
 * 2. Рассчитать среднюю норму: суммарный_расход / (суммарный_пробег / 100)
 * 3. Применить среднюю норму к пробегу по одометру (или суммарному пробегу)
 * 4. Округлить расход до сотых
 * 
 * @param input Входные данные расчёта (odometerDistance используется для итогового расчёта)
 * @returns Результат с distance и consumption
 */
export const calculateMixed = (input: FuelCalculationInput): FuelCalculationResult => {
    const { odometerDistance } = input;

    // Сначала рассчитываем как SEGMENTS (получаем детализацию модификаторов)
    const segmentsResult = calculateSegments(input);

    // Если нет маршрутов, fallback к BOILER
    if (segmentsResult.distance === 0) {
        return calculateBoiler(input);
    }

    // INV-FUEL-001: Если модификаторы не используются, возвращаемся к логике BOILER (сумма отрезков -> расчет)
    // Это нужно для того, чтобы при отсутствии галочек расчет шел строго по общему пробегу (сумма длин),
    // игнорируя погрешности округления по-сегментного расчета.
    const modifiers = segmentsResult.modifiers;
    const hasActiveModifiers = modifiers && (
        modifiers.cityLiters > 0 ||
        modifiers.warmingLiters > 0 ||
        modifiers.mountainLiters > 0
    );

    if (!hasActiveModifiers) {
        // Важный нюанс: если пользователь выбрал метод "По общему пробегу" (MIXED/BOILER) и указал
        // явный пробег по одометру (odometerDistance), мы должны его учитывать?
        // Канон гласит: "считаем расход по общему километражу... сначала суммируем километраж всех отрезков".
        // Это подразумевает, что база расчета - сумма отрезков.
        // Однако, если передан odometerDistance, возможно стоит использовать его как базу для BOILER?
        // Текущая реализация calculateBoiler считает distance = Math.round(sum(routes)).

        // По согласованию с каноном, если галочек нет -> считаем как BOILER.
        return calculateBoiler(input);
    }

    // Если есть пробег по одометру — пересчитываем расход пропорционально
    const finalDistance = odometerDistance ?? segmentsResult.distance;

    if (odometerDistance && segmentsResult.distance > 0) {
        // Пропорциональный пересчёт
        const ratio = odometerDistance / segmentsResult.distance;
        const rawConsumption = segmentsResult.consumption * ratio;
        const consumption = Math.round(rawConsumption * 100) / 100;

        // Пересчитываем модификаторы пропорционально
        const scaledModifiers = segmentsResult.modifiers ? {
            ...segmentsResult.modifiers,
            baseConsumption: Math.round(segmentsResult.modifiers.baseConsumption * ratio * 100) / 100,
            cityLiters: Math.round(segmentsResult.modifiers.cityLiters * ratio * 100) / 100,
            warmingLiters: Math.round(segmentsResult.modifiers.warmingLiters * ratio * 100) / 100,
            mountainLiters: Math.round(segmentsResult.modifiers.mountainLiters * ratio * 100) / 100
        } : undefined;

        return { distance: segmentsResult.distance, consumption, modifiers: scaledModifiers };
    }

    // Без одометра — просто возвращаем результат SEGMENTS
    return segmentsResult;
};

// ============================================================================
// УНИВЕРСАЛЬНАЯ ТОЧКА ВХОДА
// ============================================================================
/**
 * Универсальная функция расчёта топлива.
 * Выбирает алгоритм на основе указанного метода.
 * 
 * @param method Метод расчёта: 'BOILER' | 'SEGMENTS' | 'MIXED'
 * @param input Входные данные
 * @returns Результат расчёта
 */
export const calculateFuel = (
    method: FuelCalculationMethod,
    input: FuelCalculationInput
): FuelCalculationResult => {
    switch (method) {
        case 'BOILER':
            return calculateBoiler(input);
        case 'SEGMENTS':
            return calculateSegments(input);
        case 'MIXED':
            return calculateMixed(input);
        default:
            // Fallback к BOILER
            return calculateBoiler(input);
    }
};

// ============================================================================
// ВСПОМОГАТЕЛЬНЫЕ ФУНКЦИИ ДЛЯ СОВМЕСТИМОСТИ
// ============================================================================

/**
 * Совместимость с методами из batchWaybillService:
 * 'by_total' → 'BOILER'
 * 'by_segment' → 'SEGMENTS'
 */
export const mapLegacyMethod = (legacyMethod: string): FuelCalculationMethod => {
    if (legacyMethod === 'by_segment') return 'SEGMENTS';
    // INV-FUEL-001: Строгий маппинг. Если выбрано "По общему пробегу", используем BOILER.
    // Ранее использовался MIXED, но это вызывало путаницу с округлением (35.74 vs 35.72).
    // Если нужны модификаторы, пользователь должен выбрать режим "По отрезкам" (или система сама определит, если мы добавим авто-свитч).
    if (legacyMethod === 'by_total') return 'BOILER';
    return legacyMethod as FuelCalculationMethod;
};

/**
 * Рассчитывает одометр на конец.
 * Формула: Math.round(odometerStart + rawDistance)
 * Соответствует batchWaybillService: odometerEnd = Math.round(startOdo + distance)
 */
export const calculateOdometerEnd = (odometerStart: number, routes: Route[]): number => {
    const rawDistance = sumRouteDistances(routes);
    return Math.round(odometerStart + rawDistance);
};

/**
 * Рассчитывает остаток топлива на конец.
 * Формула: Math.round((start + filled - consumed) * 100) / 100
 */
export const calculateFuelEnd = (
    fuelStart: number,
    fuelFilled: number,
    fuelConsumed: number
): number => {
    const result = (fuelStart || 0) + (fuelFilled || 0) - (fuelConsumed || 0);
    return Math.round(result * 100) / 100;
};
